import * as React from "react";
import { FormSchema, FormDataChangeDetector } from "@graffer-inc/form-schema";
import {
  displayModificationType,
  ReadonlyFileRefKeyRegistry,
  ReadonlyMyNumberRefKeyRegistry,
  ApplicationEntity,
} from "@graffer-inc/cloud-gov-core";
import { ApplicationHistory } from "../../../../application/domain/application/application-history";
import { ProcedureDetailType } from "../../../../application/domain/procedures/procedure-entity";
import { formatDatetime } from "../../../../utils/date-utils";
import { BaseModal } from "../../../atoms/base-modal";
import { useReadOnlyForm } from "@graffer-inc/smart-apply";
import { SmartApplyConfirmSectionTemplateRegistry } from "../../../helpers/form-ext/registry/smart-apply-confirm-section-registry";
import { SmartApplyDiffInputRegistry } from "../../../helpers/form-ext/registry/smart-apply-diff-input-registry";
import {
  DefaultConfirmFieldTemplateRegistry,
  DefaultConfirmFunctionFieldTemplateRegistry,
  DefaultConfirmRepeatableTemplateRegistry,
  DefaultConfirmStaticFieldTemplateRegistry,
  FormGeneratorV2,
} from "@graffer-inc/form-generator";
import { corporationSearchFormBaseUrl } from "../../../../utils/corporate-search/base-url";
import { addressSearchBaseUrl } from "../../../../utils/postal-code/base-url";

type Props = {
  children?: never;
  procedure: ProcedureDetailType;
  application: ApplicationEntity;
  previous?: ApplicationHistory;
  history: ApplicationHistory;
  onClose: () => void;
};

export const ApplicationHistoryModal: React.FC<Props> = ({
  history,
  previous,
  procedure,
  onClose,
  application,
}) => {
  const props = useReadOnlyForm({
    formSchema: FormSchema.fromJSON(procedure.template.formSchema),
    validateOnInit: false,
    initialValues: history.jsonDocument,
    initialFileRegistry: history,
    initialMyNumberRegistry: history,
    formSubmissionContext: {
      submissionDate: application.createdAtDate,
    },
  });
  const username =
    history.modifiedBy.userType === "CitizenUser"
      ? "申請者"
      : history.modifiedBy.name || "職員";
  const detector =
    previous !== undefined
      ? new FormDataChangeDetector({
          current: props.values,
          original: previous.jsonDocument,
          originalFiles: new ReadonlyFileRefKeyRegistry(previous).getRegistry(),
          originalMyNumbers: new ReadonlyMyNumberRefKeyRegistry(
            previous,
          ).getRegistry(),
        })
      : undefined;

  return (
    <BaseModal isActive title="申請内容 履歴" onClose={onClose}>
      <section className="modal-card-body">
        {history.modificationType !== "INITIAL_CONTENT" && (
          <div className="message">
            <div className="message-body">
              <table>
                <tbody>
                  <Row
                    title="更新日時"
                    content={formatDatetime(history.modifiedAt)}
                  />
                  <Row title="更新者" content={username} />
                  <Row
                    title="方式"
                    content={displayModificationType(history.modificationType)}
                  />
                  <Row
                    title="訂正概要"
                    content={history.internalComment?.content}
                  />
                </tbody>
              </table>
            </div>
          </div>
        )}
        <FormGeneratorV2
          {...props}
          disable
          refkeyContext={{
            fileRefKeyExchanger: history.toFileRefKeyExchanger(),
            myNumberRefKeyExchanger: history.toMyNumberRefKeyExchanger(),
          }}
          modificationContext={{
            detector: detector,
          }}
          submissionContext={props.formSubmissionContext}
          InputElementRegistry={new SmartApplyDiffInputRegistry()}
          FieldTemplateRegistry={DefaultConfirmFieldTemplateRegistry}
          SectionTemplateRegistry={
            new SmartApplyConfirmSectionTemplateRegistry()
          }
          RepeatableTemplateRegistry={DefaultConfirmRepeatableTemplateRegistry}
          FunctionFieldTemplateRegistry={
            DefaultConfirmFunctionFieldTemplateRegistry
          }
          StaticFieldTemplateRegistry={
            DefaultConfirmStaticFieldTemplateRegistry
          }
          externalServiceUrlConfig={{
            corporationSearchFormBaseUrl: corporationSearchFormBaseUrl,
            addressSearchBaseUrl: addressSearchBaseUrl,
          }}
        />
      </section>
      <footer className="modal-card-foot">
        <button className="button is-fullwidth" onClick={onClose}>
          閉じる
        </button>
      </footer>
    </BaseModal>
  );
};

const Row = (props: { title: string; content?: string }) => {
  return (
    <tr>
      <th style={{ width: 100 }}>{props.title}</th>
      <td
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {props.content}
      </td>
    </tr>
  );
};
